<template lang="pug">
VCard.comment-teaser-card(
  tag="article"
  data-testid="comment-teaser-card"
  :lang="lang"
  :to="to"
  :link="true"
  @click="(e: any) => modalLink(e)"
)
  VCardSubtitle.pt-2
    div.d-flex.align-center.text-subtitle-2.gap-3
      div.comment-teaser-card__info.gap-3
        div.comment-teaser-card__info-brick.column-gap-3
          div.d-flex.align-center(v-if="showCommentaryType")
            CommentaryTypeIcon.mr-1(
              :machine-name="commentaryTypeMachineName"
              variant="outline"
            )
            span(v-html="commentaryTypeName")
          CommentaryDetailLevelDots(
            :length="detailLevels.length"
            :value="detailLevelNumDots"
            :name="detailLevelName"
          )
        VTooltip(
          v-if="showRanges"
          location="top"
        )
          template(#activator="activatorProps")
            span.text-ellipsis(
              v-bind="activatorProps['props']"
              v-html="formattedRangesStr || '…'"
            )
          span(v-html="formattedRangesStr || ''")

      div.ml-auto.d-flex.align-center
        CommentReadingListButton.mr-1(
          v-if="showDefaultReadingList"
          :nid="nid"
        )
        ReadingListMenu(
          v-if="showReadingListMenu"
          :comment-id="nid"
        )
          template(#activator="activatorProps")
            VBtn(
              v-bind="activatorProps['props']"
              :icon="true"
              size="small"
              variant="text"
              :title="i18n.t('ReadingList.AddReadingList')"
              :color="dark ? 'primary-c100' : 'primary-c700'"
              @click.stop.prevent
            )
              VIcon.SwipeAction__icon {{ mdiPlaylistPlus }}
        slot(name="other-actions")

  VCardTitle(
    v-if="heading || lang"
    :class="{ 'pb-3': !teaser }"
  )
    VChip.mr-2.d-inline-block.va-top(
      v-if="lang"
      size="x-small"
      :label="true"
    ) {{ lang.toUpperCase() }}
    span.text-h6(
      v-if="heading"
      v-html="heading"
    )
  VCardText.comment-teaser-card__teaser.text-body-2(v-if="teaser") {{ teaser }} …
</template>

<script setup lang="ts">
import type {
  ScriptureUtilMissingTranslationError,
  ScriptureUtilReferenceRenderError,
} from "~/units/scripture";
import { renderRangeReference } from "~/units/scripture";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { mdiPlaylistPlus } from "@mdi/js";
import { ResultAsync } from "neverthrow";
import type { ScriptureClientError } from "@rsc/scripture-client";
import { injectRequired } from "~/injectRequired";
import { useComputedResultAsync } from "~/composables/useComputedResultAsync";
import { getCommentLocation } from "~/routing/locations";
import { InModalViewKey, ScriptureClientKey } from "~/injectionKeys";
import { detailLevels } from "~/static/taxonomy/detailLevels";
import type { Props } from "~/components/CommentTeaserCard.types";
import { makeListString } from "~/units/strings";
import { useDarkMode } from "~/composables/useDarkMode";
import { getCommentaryTypeOrFallback } from "~/static/taxonomy/commentaryTypes";

const scriptureClient = injectRequired(ScriptureClientKey);

const useModalCommentLink = inject(InModalViewKey, false);

const localeRoute = useLocaleRoute();

const i18n = useI18n();
const dark = useDarkMode();
const props = withDefaults(defineProps<Props>(), {
  showCommentaryType: true,
  showRanges: true,
});

/**
 * The language of the comment, if it differs from the app language.
 * Undefined if it is the same as the app language.
 */
const lang = computed(() =>
  languageDiffer.value ? props.language : undefined,
);

const routerPath = computed(() => getCommentLocation(props.nid, lang.value));

/**
 * The path to the comment's full page.
 */
const to = computed(() => localeRoute(routerPath.value));

const modalRouter = useModalRouter();

function modalLink(e: MouseEvent | TouchEvent | KeyboardEvent): void {
  if (!to.value || e.ctrlKey || e.shiftKey || e.metaKey || e.altKey) {
    return;
  }
  if (useModalCommentLink) {
    e.preventDefault();
    modalRouter.push(to.value);
  }
}

/**
 * Whether the language of the current comment differs from the app language.
 */
const languageDiffer = computed<boolean>(
  () => i18n.locale.value !== props.language,
);

/**
 * The scripture ranges associated with the comment, e.g. "Revelation 1:1" or "Revelation 1:3a (ESV)".
 */
const formattedRanges = useComputedResultAsync<
  string[],
  | ScriptureClientError
  | ScriptureUtilReferenceRenderError
  | ScriptureUtilMissingTranslationError
>(() => {
  return ResultAsync.combine(
    props.ranges.map((range) =>
      renderRangeReference(
        range,
        scriptureClient,
        props.localTid,
        !!range.tid,
        false,
        true,
      ),
    ),
  );
});

const formattedRangesStr = computed<string>(() => {
  return makeListString(formattedRanges.value ?? []);
});

const commentaryTypeName = computed<string>(
  () =>
    getCommentaryTypeOrFallback(props.commentaryTypeMachineName).name[
      i18n.locale.value
    ] ?? "Comment",
);

const title = computed(() => {
  if (props.heading) {
    return props.heading;
  }

  if (formattedRangesStr.value) {
    return `${commentaryTypeName.value} for ${formattedRangesStr.value}`;
  }
  return commentaryTypeName.value;
});
</script>

<style lang="scss">
.comment-teaser-card__info {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (min-width: 480px) {
    flex-direction: row;
    align-items: center;
  }
}
.comment-teaser-card__info-brick {
  display: flex;
  flex-direction: column;

  @media (min-width: 600px) {
    flex-direction: row;
    align-items: center;
  }
}

.comment-teaser-card__teaser {
  mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 90%, rgba(0, 0, 0, 0));
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
